import React, { useMemo, useState } from "react";

const ROLES = [
  { id: "dispatcher", label: "Dispatcher" },
  { id: "driver", label: "Driver app" },
  { id: "parent", label: "Parent app" },
];

const AppWalkthrough = ({ operation, selectedRoute, onSelectRoute, role, onRoleChange }) => {
  const [stopIndex, setStopIndex] = useState(1);
  const route = selectedRoute || operation?.routes?.[0];
  const studentStops = useMemo(() => (route?.stops || []).filter((stop) => stop.kind === "student"), [route]);
  const currentStop = studentStops[Math.min(stopIndex, Math.max(studentStops.length - 1, 0))];
  const child = route?.students?.[0];

  if (!route) {
    return (
      <p className="start-note">
        Interactive driver and parent views need the sample operation. Use the sample roster to walk the same routes
        across dashboard and mobile.
      </p>
    );
  }

  const remaining = Math.max(studentStops.length - stopIndex, 1);

  return (
    <div className="start-walk">
      <div className="start-walk__switch" role="tablist" aria-label="Product surfaces">
        {ROLES.map((item) => (
          <button
            key={item.id}
            type="button"
            role="tab"
            aria-selected={role === item.id}
            className={role === item.id ? "is-active" : ""}
            onClick={() => onRoleChange(item.id)}
          >
            {item.label}
          </button>
        ))}
      </div>

      <p className="start-walk__shared">
        Same operation: <strong>{route.name}</strong> · {route.vehicle} · {route.driverName}
      </p>

      <div className={`start-walk__stage start-walk__stage--${role}`}>
        {role === "dispatcher" && (
          <div className="start-dispatch">
            <header>
              <h3>Dispatcher board</h3>
              <span>Morning pickup</span>
            </header>
            <ul>
              {operation.routes.map((item) => (
                <li key={item.id}>
                  <button
                    type="button"
                    className={item.id === route.id ? "is-active" : ""}
                    onClick={() => {
                      onSelectRoute(item.id);
                      setStopIndex(1);
                    }}
                  >
                    <strong>{item.name}</strong>
                    <span>
                      {item.vehicle} · {item.driverName}
                    </span>
                    <em>{item.students.length} students</em>
                  </button>
                </li>
              ))}
            </ul>
          </div>
        )}

        {role === "driver" && (
          <div className="start-phone-ui" aria-label="Driver app demonstration">
            <div className="start-phone-ui__status">
              <span>Driver</span>
              <b>On route</b>
            </div>
            <h3>{route.vehicle}</h3>
            <p>
              {route.driverName} · {route.schoolName}
            </p>
            <ol>
              {studentStops.slice(0, 6).map((stop, index) => (
                <li key={`${stop.label}-${index}`} className={index === Math.min(stopIndex, 5) ? "is-current" : ""}>
                  <span>{stop.label}</span>
                  <small>{stop.address || "Pickup"}</small>
                </li>
              ))}
            </ol>
            <button
              type="button"
              className="start-btn start-btn--compact"
              onClick={() => setStopIndex((value) => Math.min(value + 1, studentStops.length))}
            >
              Mark boarded
            </button>
          </div>
        )}

        {role === "parent" && child && (
          <div className="start-phone-ui start-phone-ui--parent" aria-label="Parent app demonstration">
            <div className="start-phone-ui__status">
              <span>Parent</span>
              <b>Live</b>
            </div>
            <h3>{child.name}</h3>
            <p>
              {route.schoolName} · {route.vehicle}
            </p>
            <div className="start-phone-ui__eta">
              <strong>{remaining}</strong>
              <span>{remaining === 1 ? "stop away" : "stops away"}</span>
            </div>
            <p className="start-muted">
              {currentStop ? `Approaching ${currentStop.label}` : "Waiting at school"}
            </p>
          </div>
        )}
      </div>
    </div>
  );
};

export default AppWalkthrough;
